import React, { Fragment, PureComponent } from 'react'
import PropTypes from 'prop-types'
import { reduxForm, formValues } from 'redux-form'
import { RadioButton, RadioButtonWithPrice, RadioButtonGroup } from '@ynap/radio-button'
import Checkbox from '@ynap/checkbox'
import Field from '@ynap/field'
import BEMHelper from '@ynap/bem-helper'
import { SimpleAsyncButton } from '@ynap/button'
import { submit } from '@ynap/form-utils'
import collateErrors from './collateErrors'
import { Notification, Tooltip } from '@ynap/notification'
import prefixName from 'redux-form/lib/util/prefixName'
import { CombinedSelect } from '@ynap/select'
import track from 'react-tracking'
import { shippingOptionsContinue, shippingMethodSelected, shippingSignatureOption, shippingMethodPremierPopup } from '../analytics/index'
import { getCrisisNotification } from '../../utils/crisis'
import { getJITNotification } from '../../utils/jit'
import { SingleBadge } from '@ynap/badge'
import { LineItem } from '@ynap/line-item'
import Price from '../Price/Price'
import PremierPopup from './PremierPopup'

const bem = new BEMHelper('ShippingOptionsForm')

const propTypes = {
    form: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired,
    error: PropTypes.string,
    submitting: PropTypes.bool,
    handleSubmit: PropTypes.func,
    change: PropTypes.func,
    className: PropTypes.string,
    shippingOptions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
            nameEN: PropTypes.string.isRequired,
            description: PropTypes.string,
            shippingDescription: PropTypes.object,
            signature: PropTypes.oneOf(['optional', 'mandatory', 'none']),
            defaultSignatureRequired: PropTypes.oneOf(['Y', 'N']),
        }),
    ).isRequired,
    messages: PropTypes.shape({
        shippingOptionsForm: PropTypes.shape({
            selectDay: PropTypes.func.isRequired,
            signatureMandatory: PropTypes.func.isRequired,
            submit: PropTypes.func.isRequired,
            noSignatureDisclaimer: PropTypes.func.isRequired,
        }).isRequired,
    }).isRequired,
    shippingOptionIdValue: PropTypes.number,
    signatureNotification: PropTypes.bool,
    enableConsidered: PropTypes.bool,
    checkoutAnalytics: PropTypes.object.isRequired,
}

const defaultProps = {
    submitting: false,
}

const renderDescription = (description, shipModeCode, consideredMessages, enableConsidered, premierMessages, handlePopup, tracking, checkoutAnalytics) => (
    <>
        {description
            .filter((line, index, arr) => arr.indexOf(line) === index)
            .map((line, index) => (
                <p className={bem('shippingLine')} key={index}>
                    {line}
                </p>
            ))}

        {enableConsidered && consideredMessages && shipModeCode === 'STANDARD' ? (
            <div className={bem('consideredWrapper')}>
                <span className={bem('considered')}>
                    <SingleBadge
                        className={bem('consideredBadge')}
                        considered={true}
                        badge={{ label: 'consideredBadge', key: 'CONSIDERED_BADGE' }}
                        translation={consideredMessages.badge()}
                    />
                </span>
                <span className={bem('consideredMessage')}>{consideredMessages.consideredDelivery()}</span>
            </div>
        ) : null}

        {(shipModeCode === 'PREMIER_DAYTIME' || shipModeCode === 'PREMIER_EVENING') && (
            <button
                type="button"
                className={bem('premierButton')}
                onClick={() => {
                    handlePopup(shipModeCode)
                    tracking.trackEvent(shippingMethodPremierPopup(checkoutAnalytics, shipModeCode))
                }}
            >
                {premierMessages?.cta()}
            </button>
        )}
    </>
)

export class ShippingOptionsForm extends PureComponent {
    constructor(props) {
        super(props)
        this.state = {
            isEIP: false,
            popup: {
                open: false,
                shipModeCode: '',
            },
        }
    }

    // temporary hack: detect EIP status to hide Next Day for non-EIPs
    async componentDidUpdate() {
        const isEIP = window?.SF?.getEipStatus && (await window.SF.getEipStatus())?.isEIP
        this.setState({ isEIP: isEIP })
    }

    handlePopup = (shipModeCode) => {
        this.setState({
            popup: {
                open: true,
                shipModeCode: shipModeCode,
            },
        })
    }

    handleShippingChange = (e, shippingOptions) => {
        const { change, _reduxForm } = this.props
        const ref = { _reduxForm }

        const shippingOptionIdValue = parseInt(e.target.value, 10)
        const selectedOption = shippingOptions.find((option) => option.id === shippingOptionIdValue)
        const { defaultNominatedSlot, defaultSignatureRequired, signature, getFormattedSlot } = selectedOption

        change(prefixName(ref, 'signatureRequired'), signature === 'mandatory' ? 'Y' : defaultSignatureRequired, shippingOptions)
        change(prefixName(ref, 'nominatedSlot'), getFormattedSlot ? defaultNominatedSlot : '', shippingOptions)
    }

    renderLineItems = (items = [], step = 1) => {
        const {
            messages,
            config: { enableQuantityStepper },
            showUsTaxUi,
        } = this.props

        return (
            <>
                <div className={bem('splitHeader')}>{messages.basket.jit.header?.({ step, uppercase: true })}</div>
                <div className={bem('splitDescription')}>{messages.basket.jit.description?.({ step })}</div>
                {items.map((item) => (
                    <LineItem
                        key={item?.orderItemId}
                        {...item}
                        messages={messages}
                        quantity={!enableQuantityStepper && item?.quantity}
                        priceComponent={Price}
                        showUsTaxUi={showUsTaxUi}
                        isJIT={item?.isJIT}
                    >
                        {item.finalSale && <Notification className={bem('finalSale')} type="info" message={messages.notifications.finalSale()} />}
                        {item.isReservation && (
                            <Notification
                                className={bem('info')}
                                type="info"
                                message={messages.basket.reservation({
                                    expiryDate: item?.reservationExpiryDate.toUpperCase(),
                                })}
                            />
                        )}
                        {item?.isHazardous && <Notification className={bem('info')} type="info" message={messages.notifications.hazmat()} />}
                    </LineItem>
                ))}
            </>
        )
    }

    renderShippingOptions = (shippingOptions, group = '') => {
        const { tracking, messages, config, enableConsidered, checkoutAnalytics, locale } = this.props
        const unavailableShippingOptions = shippingOptions.filter((option) => !option.available)
        const availableShippingOptions = shippingOptions.filter((option) => option.available)
        const consideredMessages = messages?.considered ? messages?.considered : null

        const disableFastDeliveryCountries = config?.head?.fastDeliveryDisabledCountries
        let disableFastDelivery = false
        const isEIP = this.state.isEIP

        if (!isEIP && disableFastDeliveryCountries) {
            disableFastDelivery = disableFastDeliveryCountries.includes('ALL') || disableFastDeliveryCountries.includes(locale.country.toUpperCase())
        }

        return (
            <>
                <Field
                    component={RadioButtonGroup}
                    name={`shippingOptionId${group}`}
                    parse={(value) => parseInt(value, 10)}
                    divided
                    onChange={(e) => this.handleShippingChange(e, shippingOptions)}
                >
                    {availableShippingOptions.map((option) => {
                        const { shipModeCode, id, name, description, finalShippingPrice, originalShippingPrice, getFormattedSlot } = option
                        // temporary hack - hide next day delivery for non EIPs
                        if ('NEXT_DAY_DELIVERY' === shipModeCode && disableFastDelivery) return

                        return (
                            <RadioButtonWithPrice
                                className={bem('radioButton')}
                                key={id}
                                value={id}
                                label={name}
                                description={renderDescription(
                                    description,
                                    shipModeCode,
                                    consideredMessages,
                                    enableConsidered,
                                    messages?.shippingOptionsForm.premierShippingPopup,
                                    this.handlePopup,
                                    tracking,
                                    checkoutAnalytics,
                                )}
                                onClick={() => tracking.trackEvent(shippingMethodSelected(checkoutAnalytics, option))}
                                finalShippingPrice={finalShippingPrice}
                                originalShippingPrice={originalShippingPrice}
                                verticalCenter
                            >
                                {getFormattedSlot
                                    ? ({ checked }) =>
                                          checked && (
                                              <Field
                                                  component={CombinedSelect}
                                                  label={null}
                                                  options={option.getFormattedSlot(messages.shippingOptionsForm.relativeDay)}
                                                  className={bem('nominated-slot')}
                                                  name="nominatedSlot"
                                                  emptyLabel={messages.shippingOptionsForm.selectDay()}
                                              />
                                          )
                                    : null}
                            </RadioButtonWithPrice>
                        )
                    })}
                </Field>
                {!!unavailableShippingOptions.length && (
                    <>
                        <Notification
                            className={bem('deliveryOptionsUnavailable')}
                            type="info"
                            message={messages.shippingOptionsForm.deliveryOptionsUnavailable()}
                        />
                        <Field component={RadioButtonGroup} name="unavailableShippingOptions" parse={(value) => parseInt(value, 10)} divided>
                            {unavailableShippingOptions.map(({ id, name, description }) => (
                                <RadioButton
                                    className={bem('radioButton', { disabled: 'disabled' })}
                                    key={id}
                                    label={name}
                                    description={renderDescription(description)}
                                    disabled
                                />
                            ))}
                        </Field>
                    </>
                )}
            </>
        )
    }

    render() {
        const {
            form,
            messages,
            submitting,
            error,
            handleSubmit,
            hasJITItems,
            className,
            shippingOptions,
            jitShippingOptions,
            submitFailed,
            shippingOptionIdValue,
            shippingOptionIdValueJit,
            tracking,
            crisisMessages,
            jitMessages,
            checkoutAnalytics,
            isPostSplit,
            localItems,
            remoteItems,
            locale
        } = this.props

        const selectedShippingOption = shippingOptions.find((option) => option.id === shippingOptionIdValue) || {}
        const selectedShippingOptionJit = jitShippingOptions?.find((option) => option.id === shippingOptionIdValueJit) || {}
        const crisisMessage = getCrisisNotification(bem, false, crisisMessages)
        const jitMessage = hasJITItems && !isPostSplit ? getJITNotification(jitMessages, 'shippingFormA', bem) : null
        const signaturePriority = ['mandatory', 'optional', 'none']
        const signature = [selectedShippingOption.signature, selectedShippingOptionJit.signature].sort(
            (a, b) => signaturePriority.indexOf(a) - signaturePriority.indexOf(b),
        )[0]

        return (
            <Fragment>
                <form id={form} className={bem(null, { submitting }, className)} onSubmit={handleSubmit(submit)}>
                    {(crisisMessage || jitMessage) && (
                        <div className={bem('notificationsWrapper')}>
                            {crisisMessage}
                            {jitMessage}
                        </div>
                    )}
                    {isPostSplit ? this.renderLineItems(localItems) : null}
                    {this.renderShippingOptions(shippingOptions)}
                    {isPostSplit ? (
                        <>
                            <div className={bem('postshipDivider')}>
                                <Notification type="info-globe-md" message={messages.basket.jit.postship?.()} />
                            </div>
                            {remoteItems.length ? this.renderLineItems(remoteItems, 2) : null}
                            {this.renderShippingOptions(jitShippingOptions, 'Jit')}
                        </>
                    ) : null}
                    {signature === 'optional' ? (
                        <div className={bem('noSignatureRequired')}>
                            {/* because we use checkbox here to continue control the signatureRequired, if checked, we want make signatureRequired to 'N' and unchecked is 'Y. so we add format and normalize here, this way will keep same and simple logic on shippingOptionsStep and order confirmation how we display the signatureRequired bullet points*/}
                            <Field
                                component={Checkbox}
                                name="signatureRequired"
                                onClick={(e) => tracking.trackEvent(shippingSignatureOption(checkoutAnalytics, !e.target?.checked))}
                                normalize={(value) => (value ? 'N' : 'Y')}
                                format={(value) => (value === 'Y' ? false : true)}
                            >
                                <span className={bem('noSignatureRequiredLabel')}>{messages.shippingOptionsForm.noSignatureDisclaimer()}</span>
                            </Field>
                        </div>
                    ) : signature === 'mandatory' ? (
                        <Notification className={bem('signatureMandatory')} type="info" message={messages.shippingOptionsForm.signatureMandatory()} />
                    ) : null}
                    <Tooltip
                        className={bem('buttonWrapper')}
                        type="error"
                        message={error}
                        defaultOpen={!submitting && !!submitFailed}
                        key={submitting || 'submitFailed'}
                        childWrapperClassName={'checkout'}
                    >
                        <SimpleAsyncButton
                            copy={messages.shippingOptionsForm.submit()}
                            type="primary"
                            className={bem('submit')}
                            loading={submitting}
                            onClick={() => tracking.trackEvent(shippingOptionsContinue(checkoutAnalytics))}
                        />
                    </Tooltip>
                </form>
                {this.state.popup.open && (
                    <PremierPopup
                        country={locale?.country}
                        shipModeCode={this.state.popup.shipModeCode}
                        onClose={() =>
                            this.setState((prevState) => ({
                                popup: {
                                    ...prevState.popup,
                                    open: false,
                                },
                            }))
                        }
                        messages={messages?.shippingOptionsForm?.premierShippingPopup?.popup}
                    />
                )}
            </Fragment>
        )
    }
}
ShippingOptionsForm.propTypes = propTypes
ShippingOptionsForm.defaultProps = defaultProps

export const ShippingOptionsFormWithValues = formValues({ shippingOptionIdValue: 'shippingOptionId', shippingOptionIdValueJit: 'shippingOptionIdJit' })(
    ShippingOptionsForm,
)

export default reduxForm({ form: 'shippingOptions', collateErrors })(track()(ShippingOptionsFormWithValues))
